import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileCheck, Pencil, Save, X, CheckCircle, Clock, XCircle, Ban } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { usePatient } from '@/hooks/usePatients';
import {
  useUpdatePatientConsent,
  type ConsentStatus,
  type ConsentType,
  type ConsentRecord,
} from '@/hooks/usePatientConsents';
import type { Json } from '@/types/database.generated';

interface ConsentTabProps {
  clinicId: string;
  patientId: string;
}

function parseConsent(value: Json | null): ConsentRecord | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const v = value as Record<string, Json>;
  return {
    status: (v.status as ConsentStatus) ?? 'not_set',
    type: v.type as ConsentType | undefined,
    method: v.method as 'email' | 'verbal' | 'written' | 'portal' | undefined,
    notes: typeof v.notes === 'string' ? v.notes : undefined,
    signed_at: typeof v.signed_at === 'string' ? v.signed_at : undefined,
    updated_at: typeof v.updated_at === 'string' ? v.updated_at : new Date().toISOString(),
  };
}

export function ConsentTab({ patientId }: ConsentTabProps) {
  const { t } = useTranslation();
  const { data: patient, isLoading } = usePatient(patientId);
  const updateConsent = useUpdatePatientConsent();

  const [editing, setEditing] = useState(false);
  const [formStatus, setFormStatus] = useState<ConsentStatus>('accepted');
  const [formType, setFormType] = useState<ConsentType>('gdpr');
  const [formMethod, setFormMethod] = useState<'email' | 'verbal' | 'written' | 'portal'>('written');
  const [formNotes, setFormNotes] = useState('');

  if (isLoading || !patient) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-gray-500">{t('app.loading')}</p>
      </div>
    );
  }

  const currentConsent = parseConsent(patient.consent);

  const handleSave = async () => {
    try {
      await updateConsent.mutateAsync({
        patient_id: patientId,
        consent: {
          status: formStatus,
          type: formType,
          method: formMethod,
          notes: formNotes.trim() || undefined,
          signed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      });
      toast.success(t('patients.consent.saved'));
      setEditing(false);
      setFormNotes('');
    } catch (err) {
      console.error('Failed to update consent:', err);
      toast.error(t('patients.consent.error_saving'));
    }
  };

  const getStatusColor = (status: ConsentStatus) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'rejected':
      case 'revoked':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status: ConsentStatus) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="h-4 w-4" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'rejected':
      case 'revoked':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Ban className="h-4 w-4" />;
    }
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('da-DK', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('patients.consent.title', 'Patient Consent')}
        </h3>
        {!editing && (
          <Button onClick={() => setEditing(true)} size="sm">
            <Pencil className="mr-2 h-4 w-4" />
            {t('patients.consent.record_consent', 'Record Consent')}
          </Button>
        )}
      </div>

      {/* Current Consent Status */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
        <div className="flex items-start justify-between">
          <div className="space-y-4 flex-1">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('patients.consent.status_label', 'Status')}
              </p>
              <div className="mt-1 flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(currentConsent?.status ?? 'not_set')}`}
                >
                  {getStatusIcon(currentConsent?.status ?? 'not_set')}
                  {t(`patients.consent.status.${currentConsent?.status ?? 'not_set'}`, currentConsent?.status ?? 'Not Set')}
                </span>
              </div>
            </div>

            {currentConsent?.type && (
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('patients.consent.type_label', 'Type')}
                </p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">
                  {t(`patients.consent.type.${currentConsent.type}`, currentConsent.type.replace('_', ' '))}
                </p>
              </div>
            )}

            {currentConsent?.method && (
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('patients.consent.method_label', 'Method')}
                </p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">
                  {t(`patients.consent.method.${currentConsent.method}`, currentConsent.method)}
                </p>
              </div>
            )}

            {currentConsent?.signed_at && (
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('patients.consent.signed_at', 'Signed At')}
                </p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {formatDate(currentConsent.signed_at)}
                </p>
              </div>
            )}

            {currentConsent?.notes && (
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('patients.consent.notes_label', 'Notes')}
                </p>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                  {currentConsent.notes}
                </p>
              </div>
            )}

            {currentConsent?.updated_at && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('patients.consent.last_updated', 'Last updated')}: {formatDate(currentConsent.updated_at)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Record Consent Form */}
      {editing && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-6 dark:border-indigo-800 dark:bg-indigo-900/10">
          <div className="mb-4 flex items-center gap-2">
            <FileCheck className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            <h4 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('patients.consent.record_new', 'Record New Consent')}
            </h4>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('patients.consent.status_label', 'Status')}
              </label>
              <select
                value={formStatus}
                onChange={(e) => setFormStatus(e.target.value as ConsentStatus)}
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="accepted">{t('patients.consent.status.accepted', 'Accepted')}</option>
                <option value="rejected">{t('patients.consent.status.rejected', 'Rejected')}</option>
                <option value="revoked">{t('patients.consent.status.revoked', 'Revoked')}</option>
                <option value="pending">{t('patients.consent.status.pending', 'Pending')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('patients.consent.type_label', 'Type')}
              </label>
              <select
                value={formType}
                onChange={(e) => setFormType(e.target.value as ConsentType)}
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="gdpr">{t('patients.consent.type.gdpr', 'GDPR / Data Processing')}</option>
                <option value="treatment">{t('patients.consent.type.treatment', 'Treatment Consent')}</option>
                <option value="marketing">{t('patients.consent.type.marketing', 'Marketing')}</option>
                <option value="data_processing">{t('patients.consent.type.data_processing', 'Data Processing')}</option>
                <option value="other">{t('patients.consent.type.other', 'Other')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('patients.consent.method_label', 'Method')}
              </label>
              <select
                value={formMethod}
                onChange={(e) => setFormMethod(e.target.value as 'email' | 'verbal' | 'written' | 'portal')}
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="written">{t('patients.consent.method.written', 'Written')}</option>
                <option value="verbal">{t('patients.consent.method.verbal', 'Verbal')}</option>
                <option value="email">{t('patients.consent.method.email', 'Email')}</option>
                <option value="portal">{t('patients.consent.method.portal', 'Patient Portal')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('patients.consent.notes_label', 'Notes')} ({t('common.optional', 'Optional')})
              </label>
              <textarea
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                rows={3}
                placeholder={t('patients.consent.notes_placeholder', 'Additional details about this consent...')}
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleSave} disabled={updateConsent.isPending}>
                <Save className="mr-2 h-4 w-4" />
                {updateConsent.isPending ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing(false);
                  setFormNotes('');
                }}
                disabled={updateConsent.isPending}
              >
                <X className="mr-2 h-4 w-4" />
                {t('common.cancel', 'Cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/10">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          {t(
            'patients.consent.info',
            'Record patient consent for GDPR compliance, treatment authorization, and data processing. All consent records are timestamped and auditable.',
          )}
        </p>
      </div>
    </div>
  );
}
